import React, { useEffect, useState, useCallback } from 'react';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Banknote, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import PayoutTermsSelector from '../PayoutTermsSelector';

/**
 * PayoutTermsCard (admin) — override THIS gig's payout terms (payout type +
 * timeframe) without changing the client's default. Uses the same 4-option
 * taxonomy as gig creation. Note: Critical Pay is a per-TALENT designation
 * (set on the talent's profile), NOT a gig-wide toggle, so it is intentionally
 * absent here.
 */
export const PayoutTermsCard = ({ shiftId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [terms, setTerms] = useState({});

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/admin/payout-terms/shifts/${shiftId}`);
      setData(res.data);
      setTerms({
        payout_type: res.data.payout_type || res.data?.resolved?.payout_type || 'standard',
        payout_terms_business_days: res.data.shift_override_days ?? null,
        payout_custom_note: res.data.custom_note || '',
      });
    } catch {
      // keep card usable with defaults
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        payout_type: terms.payout_type,
        custom_note: terms.payout_custom_note || null,
      };
      if (terms.payout_terms_business_days) {
        payload.business_days = parseInt(terms.payout_terms_business_days, 10);
      }
      const res = await api.patch(`/admin/payout-terms/shifts/${shiftId}`, payload);
      setData((d) => ({ ...d, resolved: res.data.resolved }));
      toast.success('Payout terms saved — talent will see the updated terms.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save payout terms');
    } finally {
      setSaving(false);
    }
  };

  const resolved = data?.resolved;

  return (
    <Card data-testid="payout-terms-editor">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Banknote className="w-5 h-5 text-emerald-600" />
          Payout Terms
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-4">
            <PayoutTermsSelector
              allowCustom
              required={false}
              value={terms}
              onChange={setTerms}
            />
            <p className="text-[11px] text-gray-400">
              Standard: leave the timeframe unset to inherit the client default
              ({data?.client_default_days ?? 30} business days). This overrides
              only this gig — it does not change the client's default.
            </p>

            {resolved && (
              <p className="text-xs text-gray-600 bg-slate-50 border border-slate-200 rounded-lg p-2.5" data-testid="payout-terms-preview">
                <span className="font-semibold">Talent sees:</span> {resolved.disclaimer}
              </p>
            )}

            <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-payout-terms-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Payout Terms
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PayoutTermsCard;
